import { Button } from '@codegouvfr/react-dsfr/Button';
import { ATTESTATION_FILE_SUB_TYPES, DELEGATE_FILE_SUB_TYPES, SIZE_10MO_OCTETS, useTextes } from '@common/config';
import {
  EMAIL_PLACEHOLDER,
  FIRST_NAME_PLACEHOLDER,
  JOB_PLACEHOLDER,
  LAST_NAME_PLACEHOLDER,
  PHONE_PLACEHOLDER,
} from '@common/schemas';
import { OrganismeFichierType, useDownloadOrganismeFichierMutation } from '@common/services';
import {
  EmailField,
  FileUploadField,

  Form,
  JPG_PNG_PDF_EXTENSIONS,
  JPG_PNG_PDF_FILE_TYPES,
  PhoneField,
  SavedFile,
  TextField,
} from '@common/ui';
import { isSchemaIncludeAnOther } from '@common/utils';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ZodObject } from 'zod';
import { useAppDispatch } from '../../../hooks/store.hook';
import { useInscriptionState } from '../../../hooks/use-inscription-state.hook';
import {
  resetUtilisateurDelegataire,
  setFichierAttestation,
  setFichierDelegation,
  setUtilisateurDelegataire,
  setUtilisateurRepresentant,
} from '../../../stores/inscription.slice';
import { StepHeader } from '../components/StepHeader';
import { useStepperInfo } from '../hooks/use-stepper-info.hook';
import {
  Step2FormResult,
  step2FormSchemaRepresentant,
  step2FormSchemaRepresentantWithDelegataire,
} from './schemas/step2-form.schema';

export const Step2 = () => {
  const t = useTextes('Inscription', 'Etape2Représentant');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const inscription = useInscriptionState();

  const [withDelegate, setWithDelegate] = useState<boolean>(!!inscription.delegataire);
  const [formSchema, setFormSchema] = useState<ZodObject>((withDelegate) ? step2FormSchemaRepresentantWithDelegataire : step2FormSchemaRepresentant);

  const [downloadOrganismeFichier] = useDownloadOrganismeFichierMutation();

  useStepperInfo({ step: 2 });

  useEffect(() => {
    if (withDelegate) {
      setFormSchema(step2FormSchemaRepresentantWithDelegataire);
    } else {
      dispatch(resetUtilisateurDelegataire());
      setFormSchema(step2FormSchemaRepresentant);
    }
  }, [withDelegate]);

  const defaultValues: Step2FormResult = {
    lastName: inscription.representant?.nom ?? '',
    firstName: inscription.representant?.prenom ?? '',
    job: inscription.representant?.travail ?? '',
    email: inscription.representant?.email ?? '',
    phone: inscription.representant?.telephone ?? '',

    legalFile: inscription.attestation,

    delegateLastName: inscription.delegataire?.nom ?? '',
    delegateFirstName: inscription.delegataire?.prenom ?? '',
    delegateJob: inscription.delegataire?.travail ?? '',
    delegateEmail: inscription.delegataire?.email ?? '',

    delegateFile: inscription.delegation,
  } as Step2FormResult;

  const saveLegalFile = (file: SavedFile | null) => dispatch(setFichierAttestation(file));
  const saveDelegateFile = (file: SavedFile | null) => dispatch(setFichierDelegation(file));

  const handleSubmit = (data: Step2FormResult) => {
    const { firstName, lastName, job, email, phone, legalFile } = data;

    dispatch(setUtilisateurRepresentant({
      nom: lastName,
      prenom: firstName,
      travail: job,
      email,
      telephone: phone,
    }));
    dispatch(setFichierAttestation(legalFile));

    if (withDelegate) {
      const { delegateLastName, delegateFirstName, delegateJob, delegateEmail, delegateFile } = data;

      dispatch(setUtilisateurDelegataire({
        nom: delegateLastName,
        prenom: delegateFirstName,
        travail: delegateJob,
        email: delegateEmail,
      }));
      dispatch(setFichierDelegation(delegateFile));
    }

    navigate('/inscription/etape-3');
  };

  return (
    <div className={'flex flex-col gap-4'}>

      <StepHeader title={'Identité du représentant légal'} text={t['INSCR_REPR_TEXT4']} />

      <Form<Step2FormResult>
        className={'flex flex-col gap-4 pt-6'}
        onSubmit={handleSubmit}
        schema={formSchema}
        defaultValues={defaultValues}>

        <TextField name={'lastName'} title={'Nom du représentant légal'} placeholder={LAST_NAME_PLACEHOLDER}
                   tooltip={t['DONNEE_OFA_COMP_RL_NOM_INFO_BULLE_MSG']} />

        <TextField name={'firstName'} title={'Prénom du représentant légal'} placeholder={FIRST_NAME_PLACEHOLDER}
                   tooltip={t['DONNEE_OFA_COMP_RL_PRENOM_INFO_BULLE_MSG']} />

        <TextField name={'job'} title={'Fonction'} placeholder={JOB_PLACEHOLDER} />

        <PhoneField name={'phone'} title={'Téléphone'} placeholder={PHONE_PLACEHOLDER}
                    tooltip={t['DONNEE_OFA_COMP_RL_TELEPHONE_INFO_BULLE_MSG']} />

        <EmailField name={'email'} title={'Courriel'} placeholder={EMAIL_PLACEHOLDER}
                    tooltip={t['DONNEE_OFA_COMP_RL_COURRIEL_INFO_BULLE_MSG']} />

        <FileUploadField name="legalFile"
                         title="Document Attestant de la qualité du représentant légal"
                         subtitle="Kbis, Statut, PV d'AG, décret ou arrêté"
                         onFileChange={saveLegalFile}
                         onFileDelete={() => saveLegalFile(null)}
                         requiredErrorMsg={t['REPR_MES11']}
                         typesSelection={ATTESTATION_FILE_SUB_TYPES}
                         extensions={JPG_PNG_PDF_EXTENSIONS}
                         fileTypes={JPG_PNG_PDF_FILE_TYPES}
                         maxSize={SIZE_10MO_OCTETS}
                         fallbackDownloadFnIfUrlIsEmpty={() => downloadOrganismeFichier({
                           id: inscription.organisme!.id,
                           fichierType: OrganismeFichierType.ATTESTATION_REPRESENTANT,
                         })} />

        <div className={'flex gap-2'}>
          <input type="checkbox" checked={withDelegate}
                 onChange={(ev) => setWithDelegate(ev.target.checked)} />
          <span>délégation de pouvoir ou de signature</span>
        </div>

        {(withDelegate && isSchemaIncludeAnOther(formSchema, step2FormSchemaRepresentantWithDelegataire)) && (
          <div className={'flex flex-col gap-4'}>

            <TextField name={'delegateLastName'}
                       title={'Nom du délégataire'}
                       placeholder={LAST_NAME_PLACEHOLDER} />

            <TextField name={'delegateFirstName'}
                       title={'Prénom du délégataire'}
                       placeholder={FIRST_NAME_PLACEHOLDER} />

            <TextField name={'delegateJob'} title={'Fonction'} placeholder={JOB_PLACEHOLDER} />

            <EmailField name={'delegateEmail'} title={'Courriel'} placeholder={EMAIL_PLACEHOLDER} />

            <FileUploadField name="delegateFile"
                             title="Délégation de pouvoir"
                             subtitle="Déclaration sur l'honneur"
                             onFileChange={saveDelegateFile}
                             onFileDelete={() => saveDelegateFile(null)}
                             requiredErrorMsg={t['REPR_MES16']}
                             typesSelection={DELEGATE_FILE_SUB_TYPES}
                             extensions={JPG_PNG_PDF_EXTENSIONS}
                             fileTypes={JPG_PNG_PDF_FILE_TYPES}
                             maxSize={SIZE_10MO_OCTETS}
                             fallbackDownloadFnIfUrlIsEmpty={() => downloadOrganismeFichier({
                               id: inscription.organisme!.id,
                               fichierType: OrganismeFichierType.DELEGATION_POUVOIR,
                             })} />
          </div>
        )}

        <div className={'flex justify-end gap-6 mt-4'}>

          <Button priority="secondary"
                  className={'uppercase'}
                  type="button"
                  onClick={() => navigate('/inscription/etape-1')}>Précédent</Button>

          <Button type="submit" className={'uppercase'}>valider</Button>
        </div>
      </Form>
    </div>
  );
};